import { type ReactNode } from 'react';
import {
	PATIENT_EDUCATION_LEVEL_LABELS,
	PATIENT_GENDER_LABELS,
	PATIENT_MARITAL_STATUS_LABELS,
} from '@/constants/patient';
import { cn } from '@/lib/utils';

export type DetailFieldItem = {
	label: string;
	value: string | null;
	fullWidth?: boolean;
};

export type DetailSection = {
	title: string;
	fields: DetailFieldItem[];
};

export const formatCpfDisplay = (cpf: string | null | undefined) => {
	if (!cpf) return null;
	const digits = cpf.replace(/\D/g, '');
	if (digits.length !== 11) return cpf;
	return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(
		6,
		9,
	)}-${digits.slice(9)}`;
};

export const formatDateDisplay = (value: string | Date | null | undefined) => {
	if (!value) return null;
	const raw = typeof value === 'string' ? value : value.toISOString();
	const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
	if (match) return `${match[3]}/${match[2]}/${match[1]}`;
	return raw;
};

export const formatPatientAgeDisplay = (
	birthDate: string | Date | null | undefined,
) => {
	if (!birthDate) return null;
	const raw =
		typeof birthDate === 'string' ? birthDate : birthDate.toISOString();
	const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
	if (!match) return null;

	const year = Number(match[1]);
	const month = Number(match[2]);
	const day = Number(match[3]);
	const today = new Date();
	let age = today.getFullYear() - year;
	const monthDiff = today.getMonth() + 1 - month;
	if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < day)) {
		age -= 1;
	}

	return age >= 0 ? `${age} anos` : null;
};

export const formatBirthWithAgeDisplay = (
	birthDate: string | Date | null | undefined,
) => {
	const dateLabel = formatDateDisplay(birthDate);
	if (!dateLabel) return null;
	const ageLabel = formatPatientAgeDisplay(birthDate);
	return ageLabel ? `${dateLabel} · ${ageLabel}` : dateLabel;
};

export const formatPhoneDisplay = (phone: string | null | undefined) => {
	if (!phone?.trim()) return null;
	const digits = phone.replace(/\D/g, '');
	if (digits.length === 11) {
		return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(
			7,
		)}`;
	}
	if (digits.length === 10) {
		return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(
			6,
		)}`;
	}
	return phone;
};

const formatAddressDisplay = (address: Clinics.Address | null | undefined) => {
	if (!address) return null;
	const parts = [
		`${address.street_name}, ${address.number}`,
		address.district_name,
		`${address.city} - ${address.state}`,
		address.postal_code,
	];
	return parts.filter(Boolean).join(' · ');
};

export const DetailField = ({
	label,
	value,
	fullWidth = false,
}: DetailFieldItem) => (
	<div className={cn('min-w-0', fullWidth && 'col-span-2')}>
		<p className="text-xs font-medium text-zinc-500">{label}</p>
		<p className="mt-1 break-words text-sm font-semibold leading-snug text-zinc-800">
			{value?.trim() ? value : '—'}
		</p>
	</div>
);

export const DetailSectionBlock = ({ title, fields }: DetailSection) => (
	<section className="rounded-xl border border-zinc-200 bg-zinc-50/50 p-3.5">
		<h3 className="border-b border-zinc-200 pb-2 text-xs font-bold uppercase tracking-wide text-zinc-600">
			{title}
		</h3>
		<div className="mt-3 grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
			{fields.map((field) => (
				<DetailField key={field.label} {...field} />
			))}
		</div>
	</section>
);

const SCHEDULING_EXCLUDED_PATIENT_SECTIONS = new Set([
	'Família',
	'Endereço',
	'Registro do prontuário',
]);

export const buildPatientDetailSections = (
	patient: Patients.Patient,
	variant: 'full' | 'scheduling' = 'full',
): DetailSection[] => {
	const childrenLabel =
		variant === 'full' &&
		patient.children?.length &&
		patient.children
			.map((child) => {
				const name = child.name?.trim();
				if (!name) return null;

				const birthDate = child.birth_date
					? formatDateDisplay(child.birth_date)
					: null;

				return birthDate ? `${name} (${birthDate})` : name;
			})
			.filter(Boolean)
			.join(', ');

	const sections: DetailSection[] = [
		{
			title: 'Identificação',
			fields: [
				{
					label: 'Nome completo',
					value: patient.full_name,
					fullWidth: true,
				},
				{ label: 'CPF', value: formatCpfDisplay(patient.cpf) },
				{ label: 'RG', value: patient.rg },
				{
					label: 'Data de nascimento',
					value: formatDateDisplay(patient.birth_date),
				},
				{
					label: 'Gênero',
					value: patient.gender
						? PATIENT_GENDER_LABELS[patient.gender]
						: null,
				},
			],
		},
		{
			title: 'Dados sociodemográficos',
			fields: [
				{
					label: 'Estado civil',
					value: patient.marital_status
						? PATIENT_MARITAL_STATUS_LABELS[patient.marital_status]
						: null,
				},
				{
					label: 'Escolaridade',
					value: patient.education_level
						? PATIENT_EDUCATION_LEVEL_LABELS[
								patient.education_level
						  ]
						: null,
				},
				{
					label: 'Profissão',
					value: patient.profession,
					fullWidth: true,
				},
			],
		},
		{
			title: 'Contato',
			fields: [
				{ label: 'Telefone', value: formatPhoneDisplay(patient.phone) },
				{ label: 'E-mail', value: patient.email },
				{
					label: 'Contato de emergência',
					value: patient.emergency_contact,
					fullWidth: true,
				},
			],
		},
		{
			title: 'Responsável legal',
			fields: [
				{
					label: 'Nome do responsável',
					value: patient.guardian_full_name,
					fullWidth: true,
				},
			],
		},
		{
			title: 'Família',
			fields: [
				{
					label: 'Filhos',
					value: childrenLabel || null,
					fullWidth: true,
				},
			],
		},
		{
			title: 'Endereço',
			fields: [
				{
					label: 'Endereço completo',
					value: formatAddressDisplay(patient.address),
					fullWidth: true,
				},
			],
		},
		{
			title: 'Registro do prontuário',
			fields: [
				{
					label: 'Data de abertura',
					value: formatDateDisplay(patient.opened_at),
					fullWidth: true,
				},
			],
		},
	];

	if (variant === 'scheduling') {
		return sections.filter(
			(section) =>
				!SCHEDULING_EXCLUDED_PATIENT_SECTIONS.has(section.title),
		);
	}

	return sections;
};

export const buildUserDetailSections = (user: Users.User): DetailSection[] => {
	const fullName =
		`${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || null;

	return [
		{
			title: 'Identificação',
			fields: [
				{ label: 'Nome completo', value: fullName, fullWidth: true },
				{ label: 'CPF', value: formatCpfDisplay(user.cpf) },
				{
					label: 'Data de nascimento',
					value: formatDateDisplay(user.birthday),
				},
			],
		},
		{
			title: 'Contato',
			fields: [
				{ label: 'E-mail', value: user.email ?? null },
				{
					label: 'Telefone',
					value: formatPhoneDisplay(user.phone_number),
				},
			],
		},
	];
};

export const flattenDetailSections = (
	sections: DetailSection[],
): DetailFieldItem[] => sections.flatMap((section) => section.fields);

export const ParticipantDetailSections = ({
	sections,
}: {
	sections: DetailSection[];
}) => (
	<div className="space-y-3">
		{sections.map((section) => (
			<DetailSectionBlock key={section.title} {...section} />
		))}
	</div>
);

export const ParticipantDetailFieldsList = ({
	fields,
}: {
	fields: DetailFieldItem[];
}) => (
	<div className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
		{fields.map((field) => (
			<DetailField key={field.label} {...field} />
		))}
	</div>
);

export const ParticipantDetailLoading = () => (
	<p className="text-sm text-zinc-400">Carregando informações...</p>
);

export const ParticipantDetailError = ({
	children,
}: {
	children: ReactNode;
}) => <p className="text-sm text-zinc-500">{children}</p>;
